import { useState } from 'react';
import { useNavigate, Link, NavLink } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, Sparkles } from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { API_URLS, IN_APP_ROUTES } from '@/constants/urls';
import { useAuth } from '@/contexts/AuthContext';
import GoogleOauth from '@/components/auth/GoogleOauth';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';
import ScrollToTop from '@/components/ScrollToTop';
import { useCompset } from '@/contexts/CompsetContext';


export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { cleanUpAccount } = useAuth();

  const { handleLogin: onSuccessfulLogin } = useAuth()
  const { setActiveCompset } = useCompset();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setActiveCompset(null);
    cleanUpAccount();

    try {
      const result = await apiClient.post(API_URLS.LOGIN, {
        email: email,
        password: password
      })

      const data = result.data

      if (result?.status == 200) {
        onSuccessfulLogin(data)
      }

    } catch (error) {
      apiErrorHandler(error)
    } finally {
      setIsLoading(false);
    }

  };


  return (
    <div className="min-h-screen flex">
      <ScrollToTop />
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 hero-gradient relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 lg:px-16">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-xl bg-primary-foreground/10 backdrop-blur-sm flex items-center justify-center font-heading font-bold text-xl text-primary-foreground border border-primary-foreground/20">
              P
            </div>
            <span className="font-heading font-semibold text-2xl text-primary-foreground">
              Prowen
            </span>
          </div>

          <h1 className="font-heading text-4xl lg:text-5xl font-bold text-primary-foreground mb-6 leading-tight">
            Make Smarter<br />
            <span className="text-accent-light">Pricing Decisions</span>
          </h1>

          <p className="text-lg text-primary-foreground/80 mb-8 max-w-md">
            Real-time competitor tracking, review monitoring, and market trend analysis for modern hotel portfolios.
          </p>

          {/* Features */}
          <div className="space-y-4">
            {[
              'Competitor Rate Tracking',
              'Review & Rankings Monitoring',
              'Real-Time Market Trends',
              'AI-Powered Insights'
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-3 text-primary-foreground/80">
                <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-accent" />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute top-20 right-20 w-64 h-64 bg-primary-light/10 rounded-full blur-3xl" />
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center font-heading font-bold text-primary-foreground">
              P
            </div>
            <span className="font-heading font-semibold text-xl text-foreground">
              Prowen
            </span>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-heading">Welcome back</CardTitle>
              <CardDescription>
                Sign in to your account to continue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <NavLink to={IN_APP_ROUTES.FORGOT_PASSWORD_PAGE} className="text-xs text-primary hover:underline">
                      Forgot password?
                    </NavLink>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Remember Me */}
                {/* <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember"
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                  />
                  <label
                    htmlFor="remember"
                    className="text-sm text-muted-foreground cursor-pointer"
                  >
                    Remember me for 30 days
                  </label>
                </div> */}

                {/* Submit Button */}
                <Button type="submit" className="w-full" size="lg" disabled={isLoading} data-testid="login-submit-btn">
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or</span>
                  </div>
                </div>
              </form>

              <div className='mt-2'>
                <GoogleOauth />
              </div>

              {/* Sign Up Link */}
              <p className="text-center text-sm text-muted-foreground mt-6">
                Don&apos;t have an account?{' '}
                <Link to="/register" className="text-primary hover:underline font-medium">
                  Create Account
                </Link>
              </p>
            </CardContent>
          </Card>

          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground mt-6">
            {`© ${new Date().getFullYear()}  Prowen Technologies India Private Limited`}
          </p>
        </div>
      </div>
    </div>
  );
}
